/**
 * Formatting toolbar at the top of a section in WYSIWYM mode (spec §8.19): a ProseMirror plugin view
 * (imperative, I6), so it follows the editor state without React re-renders.
 */
import { lift, setBlockType, toggleMark, wrapIn } from "prosemirror-commands";
import type { MarkType, NodeType } from "prosemirror-model";
import { liftListItem, sinkListItem, wrapInList } from "prosemirror-schema-list";
import { Plugin, type Command, type EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import { redo, undo } from "loro-prosemirror";
import { safeHref } from "./markdown";
import { pmSchema as s } from "./pmSchema";
import type { Translate } from "../i18n";

interface Item {
  id: string;
  label: string;
  title: string;
  run: Command | ((view: EditorView) => void);
  active?: (state: EditorState) => boolean;
  enabled?: (state: EditorState) => boolean;
  className?: string;
}

function markActive(state: EditorState, type: MarkType): boolean {
  const { from, $from, to, empty } = state.selection;
  if (empty) return !!type.isInSet(state.storedMarks || $from.marks());
  return state.doc.rangeHasMark(from, to, type);
}

function blockActive(state: EditorState, type: NodeType, attrs?: Record<string, unknown>): boolean {
  return state.selection.$from.parent.hasMarkup(type, attrs);
}

function inAncestor(state: EditorState, type: NodeType): boolean {
  const $from = state.selection.$from;
  for (let d = $from.depth; d > 0; d--) if ($from.node(d).type === type) return true;
  return false;
}

const insertRule: Command = (state, dispatch) => {
  dispatch?.(state.tr.replaceSelectionWith(s.nodes.horizontal_rule!.create()).scrollIntoView());
  return true;
};

const toggleBlockquote: Command = (state, dispatch) => (inAncestor(state, s.nodes.blockquote!) ? lift(state, dispatch) : wrapIn(s.nodes.blockquote!)(state, dispatch));

function items(t: Translate): Item[][] {
  const mark = (id: string, label: string, title: string, type: MarkType, className?: string): Item => ({ id, label, title, run: toggleMark(type), active: (st) => markActive(st, type), className });
  const heading = (level: number): Item => ({ id: `h${level}`, label: `H${level}`, title: t("tb.heading", { level }), run: setBlockType(s.nodes.heading!, { level }), active: (st) => blockActive(st, s.nodes.heading!, { level }) });
  return [
    [
      mark("bold", "B", t("tb.bold"), s.marks.strong!, "font-bold"),
      mark("italic", "I", t("tb.italic"), s.marks.em!, "italic"),
      mark("code", "</>", t("tb.code"), s.marks.code!, "font-mono"),
      mark("strike", "S", t("tb.strike"), s.marks.strike!, "line-through"),
      { id: "link", label: "🔗", title: t("tb.link"), run: (view: EditorView) => editLink(view, t), active: (st) => markActive(st, s.marks.link!) },
    ],
    [{ id: "paragraph", label: "¶", title: t("tb.paragraph"), run: setBlockType(s.nodes.paragraph!), active: (st) => blockActive(st, s.nodes.paragraph!) }, heading(1), heading(2), heading(3)],
    [
      { id: "bullet", label: "•", title: t("tb.bullet"), run: wrapInList(s.nodes.bullet_list!), active: (st) => inAncestor(st, s.nodes.bullet_list!) },
      { id: "ordered", label: "1.", title: t("tb.ordered"), run: wrapInList(s.nodes.ordered_list!), active: (st) => inAncestor(st, s.nodes.ordered_list!) },
      { id: "indent", label: "⇥", title: t("tb.indent"), run: sinkListItem(s.nodes.list_item!) },
      { id: "outdent", label: "⇤", title: t("tb.outdent"), run: liftListItem(s.nodes.list_item!) },
    ],
    [
      { id: "quote", label: "❝", title: t("tb.quote"), run: toggleBlockquote, active: (st) => inAncestor(st, s.nodes.blockquote!), enabled: () => true },
      { id: "rule", label: "―", title: t("tb.rule"), run: insertRule },
    ],
    [
      { id: "undo", label: "↶", title: t("tb.undo"), run: undo },
      { id: "redo", label: "↷", title: t("tb.redo"), run: redo },
    ],
  ];
}

/** Ask for a URL (http, https, mailto) and set or remove the link on the selection. */
function editLink(view: EditorView, t: Translate): void {
  const type = s.marks.link!;
  const { state } = view;
  if (markActive(state, type) && state.selection.empty) {
    // a caret inside a link: extend to the whole link
    const $pos = state.selection.$from;
    const range = markRange($pos.parent, $pos.parentOffset, type);
    if (range) {
      const start = $pos.start();
      view.dispatch(state.tr.removeMark(start + range.from, start + range.to, type));
      return;
    }
  }
  const current = state.doc.rangeHasMark(state.selection.from, state.selection.to, type);
  const raw = window.prompt(current ? t("tb.linkPromptEdit") : t("tb.linkPrompt"), "https://");
  if (raw === null) return;
  const { from, to } = view.state.selection;
  if (!raw.trim() || raw.trim() === "https://") {
    view.dispatch(view.state.tr.removeMark(from, to, type));
  } else {
    const href = safeHref(raw);
    if (!href) {
      window.alert(t("tb.linkInvalid"));
      return;
    }
    if (from === to) view.dispatch(view.state.tr.insertText(href, from).addMark(from, from + href.length, type.create({ href })));
    else view.dispatch(view.state.tr.removeMark(from, to, type).addMark(from, to, type.create({ href })));
  }
  view.focus();
}

function markRange(parent: import("prosemirror-model").Node, offset: number, type: MarkType): { from: number; to: number } | null {
  let start = -1;
  let end = -1;
  let pos = 0;
  parent.forEach((child) => {
    const childEnd = pos + child.nodeSize;
    if (type.isInSet(child.marks)) {
      if (start < 0) start = pos;
      end = childEnd;
    } else if (childEnd <= offset || pos >= offset) {
      if (end < offset) start = -1;
    }
    pos = childEnd;
  });
  return start >= 0 && start <= offset && end >= offset ? { from: start, to: end } : null;
}

/** `t` translates the titles (spec §8.23): an imperative view cannot use the React hook. */
export function toolbarPlugin(t: Translate): Plugin {
  return new Plugin({
    view(editorView) {
      const bar = document.createElement("div");
      bar.className = "section-toolbar mb-1 flex flex-wrap items-center gap-0.5 rounded-sm border border-neutral-200 bg-[var(--surface-2)] px-1.5 py-1 text-xs dark:border-neutral-800";
      bar.setAttribute("role", "toolbar");
      bar.setAttribute("aria-label", t("tb.formatting"));
      bar.dataset.testid = "section-toolbar";
      const buttons: Array<{ el: HTMLButtonElement; item: Item }> = [];
      items(t).forEach((group, gi) => {
        if (gi > 0) {
          const sep = document.createElement("span");
          sep.className = "mx-0.5 h-4 w-px bg-neutral-200 dark:bg-neutral-700";
          bar.appendChild(sep);
        }
        for (const item of group) {
          const el = document.createElement("button");
          el.type = "button";
          el.textContent = item.label;
          el.title = item.title;
          el.setAttribute("aria-label", item.title);
          el.dataset.testid = `tb-${item.id}`;
          el.className = `min-w-6 rounded-sm px-1.5 py-0.5 text-neutral-600 hover:bg-neutral-100 disabled:opacity-30 aria-pressed:bg-neutral-200 aria-pressed:text-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:aria-pressed:bg-neutral-700 ${item.className ?? ""}`;
          // keep the editor's selection: act on mousedown and never take focus
          el.addEventListener("mousedown", (e) => {
            e.preventDefault();
            const run = item.run as (a: EditorState | EditorView, b?: EditorView["dispatch"], c?: EditorView) => boolean | void;
            if (item.id === "link") (item.run as (v: EditorView) => void)(editorView);
            else run(editorView.state, editorView.dispatch, editorView);
            editorView.focus();
          });
          buttons.push({ el, item });
          bar.appendChild(el);
        }
      });
      editorView.dom.parentNode?.insertBefore(bar, editorView.dom);
      const update = (view: EditorView) => {
        for (const { el, item } of buttons) {
          if (item.active) el.setAttribute("aria-pressed", String(item.active(view.state)));
          const enabled = item.enabled ? item.enabled(view.state) : item.id === "link" || (item.run as Command)(view.state);
          el.disabled = !enabled;
        }
      };
      update(editorView);
      return { update, destroy: () => bar.remove() };
    },
  });
}
